/**
 * Founder Control Center v2 · Sección 2 · Intelligence Diagnostics.
 *
 * Vista de salud técnica del subsistema RAG / Copilot AG-07.
 *
 * Fuentes (sólo lectura · 0 cambios backend):
 *   GET /api/founder-os/copilot/health       → KB docs, chunks, embedder, BM25, reindex job
 *   GET /api/founder-os/copilot/diagnostics  → memoria, disco, red, embedder, fastembed cache
 *
 * Decisiones de diseño:
 *   - Read-only: sin botones de mutación (esos viven en KB Manager).
 *   - Manual refresh (no polling) para no martillear el backend.
 *   - Cada bloque degrada a "no disponible" si su endpoint falla.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import {
  Pulse, Cpu, Database, CheckCircle, Warning, ArrowsClockwise, Lightning,
  HardDrive, Heartbeat, Globe,
} from "@phosphor-icons/react";

export default function Diagnostics() {
  const [health, setHealth] = useState(null);
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState({ health: null, diag: null });

  const load = async () => {
    setLoading(true);
    const [h, d] = await Promise.allSettled([
      api.get("/founder-os/copilot/health"),
      api.get("/founder-os/copilot/diagnostics"),
    ]);
    setHealth(h.status === "fulfilled" ? h.value.data : null);
    setDiag(d.status === "fulfilled" ? d.value.data : null);
    setErr({
      health: h.status === "rejected" ? (h.reason?.response?.data?.detail || "no disponible") : null,
      diag:   d.status === "rejected" ? (d.reason?.response?.data?.detail || "no disponible") : null,
    });
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="diagnostics-root">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
            <Heartbeat size={11} weight="fill" /> System Health · AG-07 RAG
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[var(--ag-text)] mt-1">
            Diagnostics
          </h2>
          <p className="text-xs text-[var(--ag-text-dim)] mt-1">
            Lectura read-only del estado del Copilot RAG, memoria del proceso, disco y red. Las mutaciones (reindex / load baked / upload) viven en <strong>KB Manager</strong>.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] px-3 py-2 rounded border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/50 transition-colors disabled:opacity-50"
          data-testid="diagnostics-refresh"
        >
          <ArrowsClockwise size={13} className={loading ? "animate-spin" : ""} /> Refrescar
        </button>
      </div>

      {/* Overall banner */}
      {health?.overall && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm ${
            health.overall === "healthy" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" :
            health.overall === "degraded" ? "bg-amber-500/10 border-amber-500/30 text-amber-300" :
            health.overall === "warning"  ? "bg-amber-500/10 border-amber-500/30 text-amber-300" :
            "bg-red-500/10 border-red-500/30 text-red-300"
          }`}
          data-testid="diagnostics-overall"
        >
          <div className="flex items-center gap-2">
            {health.overall === "healthy"
              ? <CheckCircle size={16} weight="fill" />
              : <Warning size={16} weight="fill" />}
            <strong className="uppercase tracking-wider text-[10px] mr-1">{health.overall}</strong>
            <span className="text-xs">{health.message || "sin mensaje"}</span>
          </div>
        </div>
      )}

      {/* RAG Health */}
      <section className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4" data-testid="diagnostics-rag">
        <div className="flex items-center gap-2 mb-3">
          <Database size={14} weight="duotone" className="text-[var(--ag-gold)]" />
          <h3 className="text-xs uppercase tracking-[0.22em] text-[var(--ag-text-dim)] font-semibold">
            RAG · Knowledge Base
          </h3>
        </div>
        {err.health && (
          <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1.5 mb-3" data-testid="diagnostics-health-error">
            Error cargando health: {err.health}
          </div>
        )}
        {!health && !err.health && loading && (
          <div className="text-xs text-[var(--ag-text-dim)]">Cargando…</div>
        )}
        {health && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBox
              icon={<Database size={13} />}
              label="KB Docs"
              value={health.kb_documents?.count ?? 0}
              tone="info"
              testid="diag-kb-docs"
            />
            <StatBox
              icon={<HardDrive size={13} />}
              label="Chunks (embed / total)"
              value={`${health.chunks?.with_embeddings ?? 0} / ${health.chunks?.total ?? 0}`}
              tone={health.chunks?.with_embeddings === health.chunks?.total && (health.chunks?.total ?? 0) > 0 ? "ok" : "warn"}
              testid="diag-chunks"
            />
            <StatBox
              icon={health.embedder?.loaded ? <CheckCircle size={13} /> : <Warning size={13} />}
              label="Embedder"
              value={health.embedder?.loaded ? "Cargado" : (health.embedder?.load_failed ? "Falló" : "No cargado")}
              tone={health.embedder?.loaded ? "ok" : (health.embedder?.load_failed ? "fail" : "neutral")}
              testid="diag-embedder"
            />
            <StatBox
              icon={health.bm25?.ready ? <CheckCircle size={13} /> : <Warning size={13} />}
              label="BM25"
              value={health.bm25?.ready ? `Ready · ${health.bm25?.indexed_chunks ?? 0}` : "Pendiente"}
              tone={health.bm25?.ready ? "ok" : "neutral"}
              testid="diag-bm25"
            />
          </div>
        )}
        {health?.reindex_job?.running && (
          <div className="mt-4 p-3 rounded bg-[var(--ag-gold)]/5 border border-[var(--ag-gold)]/30" data-testid="diag-reindex">
            <div className="flex items-center gap-2 text-xs text-[var(--ag-gold)]">
              <ArrowsClockwise size={12} className="animate-spin" />
              <strong>Reindexando · modo {health.reindex_job.mode || "init"}</strong>
              <span className="text-[10px] text-[var(--ag-text-dim)] ml-auto">
                {health.reindex_job.files_done}/{health.reindex_job.files_total || "?"} archivos · {health.reindex_job.chunks_total ?? 0} chunks
              </span>
            </div>
            <div className="mt-2 h-1.5 rounded-full bg-[var(--ag-bg)] overflow-hidden">
              <div className="h-full bg-[var(--ag-gold)] transition-all" style={{
                width: health.reindex_job.files_total > 0
                  ? `${Math.round((health.reindex_job.files_done / health.reindex_job.files_total) * 100)}%`
                  : "10%",
              }} />
            </div>
          </div>
        )}
      </section>

      {/* Process · Memory / Disk / Embedder flag */}
      <section className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4" data-testid="diagnostics-process">
        <div className="flex items-center gap-2 mb-3">
          <Cpu size={14} weight="duotone" className="text-[var(--ag-gold)]" />
          <h3 className="text-xs uppercase tracking-[0.22em] text-[var(--ag-text-dim)] font-semibold">
            Process · Memoria · Disco · Embedder
          </h3>
        </div>
        {err.diag && (
          <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded px-2 py-1.5 mb-3" data-testid="diagnostics-diag-error">
            Error cargando diagnostics: {err.diag}
          </div>
        )}
        {!diag && !err.diag && loading && (
          <div className="text-xs text-[var(--ag-text-dim)]">Cargando…</div>
        )}
        {diag && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatBox
                icon={<Cpu size={13} />}
                label="Proceso RSS"
                value={diag.memory?.process_rss_mb != null ? `${Math.round(diag.memory.process_rss_mb)} MB` : "—"}
                tone={diag.memory?.process_rss_mb != null && diag.memory.process_rss_mb > 800 ? "warn" : "ok"}
                testid="diag-memory-rss"
              />
              <StatBox
                icon={<HardDrive size={13} />}
                label="Sistema · uso RAM"
                value={diag.memory?.system_used_percent != null ? `${diag.memory.system_used_percent.toFixed(1)}%` : "—"}
                tone={
                  diag.memory?.system_used_percent == null ? "neutral" :
                  diag.memory.system_used_percent < 70 ? "ok" :
                  diag.memory.system_used_percent < 85 ? "warn" : "fail"
                }
                testid="diag-memory-system"
              />
              <StatBox
                icon={<HardDrive size={13} />}
                label="Disco libre"
                value={diag.disk?.free_gb != null ? `${diag.disk.free_gb.toFixed(2)} GB` : "—"}
                tone={
                  diag.disk?.used_percent == null ? "neutral" :
                  diag.disk.used_percent < 70 ? "ok" :
                  diag.disk.used_percent < 85 ? "warn" : "fail"
                }
                testid="diag-disk"
              />
              <StatBox
                icon={<Lightning size={13} />}
                label="Embedder enabled"
                value={diag.embedder?.enabled === true ? "true" : diag.embedder?.enabled === false ? "false" : "—"}
                tone={diag.embedder?.enabled === false ? "warn" : "ok"}
                testid="diag-flag-emb"
              />
            </div>

            {/* Embedder detail */}
            {diag.embedder && (
              <div className="mt-4 grid sm:grid-cols-2 gap-3 text-[11px] text-[var(--ag-text-dim)]" data-testid="diag-embedder-detail">
                <div className="rounded border border-[var(--ag-border)] px-3 py-2">
                  <div className="text-[9px] uppercase tracking-wider text-[var(--ag-text-muted)]">Modelo</div>
                  <div className="font-mono mt-0.5">{diag.embedder?.model || "—"}</div>
                  <div className="text-[10px] mt-1">dim: <span className="tabular-nums">{diag.embedder?.dim ?? "—"}</span></div>
                </div>
                <div className="rounded border border-[var(--ag-border)] px-3 py-2">
                  <div className="text-[9px] uppercase tracking-wider text-[var(--ag-text-muted)]">Warmup</div>
                  <div className="mt-0.5">
                    started: <code className="text-[var(--ag-text)]">{String(diag.embedder?.warmup_started ?? false)}</code> ·
                    loaded: <code className="text-[var(--ag-text)]">{String(diag.embedder?.loaded ?? false)}</code> ·
                    failed: <code className="text-[var(--ag-text)]">{String(diag.embedder?.load_failed ?? false)}</code>
                  </div>
                  {diag.embedder?.loaded_at && (
                    <div className="text-[10px] mt-1">loaded_at: <code className="text-[var(--ag-text-muted)]">{diag.embedder.loaded_at}</code></div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* Network connectivity */}
      {diag?.network && Array.isArray(diag.network) && diag.network.length > 0 && (
        <section className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4" data-testid="diagnostics-network">
          <div className="flex items-center gap-2 mb-3">
            <Globe size={14} weight="duotone" className="text-[var(--ag-gold)]" />
            <h3 className="text-xs uppercase tracking-[0.22em] text-[var(--ag-text-dim)] font-semibold">
              Network · alcance integración
            </h3>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {diag.network.map((n, i) => (
              <div
                key={`${n.target}-${i}`}
                className={`rounded border px-3 py-2 text-[11px] ${
                  n.reachable
                    ? "border-emerald-500/30 bg-emerald-500/5"
                    : "border-red-500/30 bg-red-500/5"
                }`}
                data-testid={`diag-net-${n.target?.replace(/[^a-z0-9]/gi, "_")}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-mono text-[10px] text-[var(--ag-text)] truncate">{n.target}</div>
                  <span className={`text-[9px] uppercase tracking-wider ${n.reachable ? "text-emerald-300" : "text-red-300"}`}>
                    {n.reachable ? "OK" : "FAIL"}
                  </span>
                </div>
                <div className="text-[10px] text-[var(--ag-text-dim)] mt-1 flex items-center justify-between">
                  <span>HTTP {n.http_status ?? "—"}</span>
                  <span className="tabular-nums">{n.elapsed_ms ?? "—"} ms</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Fastembed cache + raw JSON */}
      {diag && (
        <section className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4 text-[11px] text-[var(--ag-text-dim)]" data-testid="diagnostics-raw">
          <details>
            <summary className="cursor-pointer hover:text-[var(--ag-gold)] inline-flex items-center gap-1">
              <Database size={11} /> Fastembed cache checked paths
            </summary>
            <pre className="mt-2 p-3 rounded bg-[var(--ag-bg)] border border-[var(--ag-border)] overflow-x-auto text-[10px]">
{JSON.stringify(diag.fastembed_cache, null, 2)}
            </pre>
          </details>
          <details className="mt-2">
            <summary className="cursor-pointer hover:text-[var(--ag-gold)] inline-flex items-center gap-1">
              <Pulse size={11} /> Diagnostics raw JSON
            </summary>
            <pre className="mt-2 p-3 rounded bg-[var(--ag-bg)] border border-[var(--ag-border)] overflow-x-auto text-[10px]">
{JSON.stringify(diag, null, 2)}
            </pre>
          </details>
          {diag.timestamp && (
            <div className="text-[10px] text-[var(--ag-text-muted)] mt-2">
              Snapshot: {new Date(diag.timestamp).toLocaleString("es-ES")}
            </div>
          )}
        </section>
      )}

      <p className="text-[10px] text-[var(--ag-text-muted)] text-center pt-2">
        Endpoints: /api/founder-os/copilot/health · /api/founder-os/copilot/diagnostics ·
        Read-only · Las mutaciones viven en KB Manager.
      </p>
    </div>
  );
}

function StatBox({ icon, label, value, tone = "neutral", testid }) {
  const tones = {
    ok:      "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
    warn:    "border-amber-500/30 bg-amber-500/10 text-amber-300",
    fail:    "border-red-500/30 bg-red-500/10 text-red-300",
    info:    "border-[var(--ag-gold)]/30 bg-[var(--ag-gold)]/10 text-[var(--ag-gold)]",
    neutral: "border-[var(--ag-border)] bg-[var(--ag-midnight)]/30 text-[var(--ag-text-dim)]",
  };
  return (
    <div className={`rounded border px-3 py-2 ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-80">
        {icon} {label}
      </div>
      <div className="text-sm font-bold tabular-nums mt-0.5">{value}</div>
    </div>
  );
}
